import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../hooks/useAuth";
import { errorMessage } from "../hooks/useServers";

const CHANNEL = { whatsapp: "WhatsApp", email: "correo" };

// Pantalla de acceso en pasos (password → code → alt → recovery | otp, y forgot). Los mensajes de error vienen del servidor, ya en español.
export default function Login() {
  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState("");
  const [methods, setMethods] = useState({});
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [pin, setPin] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [sentTo, setSentTo] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const auth = useAuth();
  const navigate = useNavigate();

  const go = (next) => {
    setStep(next);
    setCode("");
    setPin("");
    setError("");
  };
  const run = async (action, fallbackMessage) => {
    setLoading(true);
    setError("");
    try {
      return await action();
    } catch (err) {
      setError(errorMessage(err, fallbackMessage));
      return null;
    } finally {
      setLoading(false);
    }
  };
  const done = () => navigate("/");

  const submitPassword = async (e) => {
    e.preventDefault();
    const result = await run(() => auth.login(email, password), "Credenciales inválidas");
    if (!result) return;
    if (result.status === "ok") return done();
    setChallenge(result.challenge);
    setMethods(result.methods || {});
    setNotice("");
    go("code");
  };
  const submitCode = async (e) => {
    e.preventDefault();
    const result = await run(() => auth.loginTotp(challenge, code, remember), "Código incorrecto.");
    if (result) done();
  };
  const submitRecovery = async (e) => {
    e.preventDefault();
    const result = await run(() => auth.loginRecoveryCode(challenge, code), "Ese código no sirve.");
    if (result) done();
  };
  const sendCode = async (forgot = false) => {
    const sent = await run(() => (forgot ? auth.forgotRequest(email, channel) : auth.fallbackRequest(challenge, channel)), "No se pudo enviar el código.");
    if (sent) {
      setSentTo(sent.to || "");
      setNotice(forgot && !sent.to ? "Si la cuenta existe, el código va en camino." : `Te mandamos un código por ${CHANNEL[channel]}${sent.to ? ` (${sent.to})` : ""}. Vale 10 minutos.`);
    }
  };
  const submitOtp = async (e) => {
    e.preventDefault();
    const result = await run(() => auth.fallbackVerify(challenge, channel, code, pin), "El código o el PIN no coinciden.");
    if (result) done();
  };
  const submitForgot = async (e) => {
    e.preventDefault();
    const result = await run(() => auth.forgotConfirm(email, channel, code, pin, newPassword), "No se pudo cambiar la contraseña.");
    if (result) {
      setPassword("");
      setNewPassword("");
      setNotice("Listo: cambiaste tu contraseña. Ahora inicia sesión con la nueva.");
      go("password");
    }
  };

  const title = { password: "Acceso administrativo", code: "Verificación en 2 pasos", alt: "No puedo usar mi código", recovery: "Código de recuperación", otp: "Recuperar el acceso", forgot: "Olvidé mi contraseña" }[step];
  const submitting = (label, busy) => (loading ? busy : label);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="font-mono text-2xl font-bold text-emerald-500 tracking-tight">Panel Orquestador</h1>
          <p data-testid="login-title" className="text-muted-foreground text-sm mt-1">{title}</p>
        </div>
        <div className="space-y-4 bg-card border border-border rounded-xl p-6">
          {notice && <p data-testid="login-notice" className="text-xs text-emerald-500">{notice}</p>}

          {step === "password" && (
            <form data-testid="login-form" onSubmit={submitPassword} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-muted-foreground text-xs">Email</Label>
                <Input data-testid="login-email-input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-muted-foreground text-xs">Contraseña</Label>
                <Input data-testid="login-password-input" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                {submitting("Ingresar", "Ingresando...")}
              </Button>
              <button type="button" data-testid="login-forgot-link" className="block w-full text-center text-xs text-muted-foreground underline" onClick={() => { setNotice(""); setChannel("whatsapp"); go("forgot"); }}>
                Olvidé mi contraseña
              </button>
            </form>
          )}

          {step === "code" && (
            <form data-testid="login-code-form" onSubmit={submitCode} className="space-y-4">
              <p className="text-xs text-muted-foreground">Escribe el código de 6 dígitos que muestra Google Authenticator para «Panel Orquestador».</p>
              <Input data-testid="login-code-input" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={7} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value)} className="text-center text-lg tracking-widest" />
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input data-testid="login-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Confiar en este dispositivo 30 días
              </label>
              <Button data-testid="login-code-submit" type="submit" disabled={loading || code.replace(/\D/g, "").length !== 6} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                {submitting("Verificar", "Verificando...")}
              </Button>
              <button type="button" data-testid="login-alt-link" className="block w-full text-center text-xs text-muted-foreground underline" onClick={() => go("alt")}>
                No puedo usar mi código
              </button>
            </form>
          )}

          {step === "alt" && (
            <div data-testid="login-alt" className="space-y-3">
              <p className="text-xs text-muted-foreground">Elige cómo quieres entrar sin Google Authenticator.</p>
              {methods.recovery_codes && (
                <Button data-testid="login-alt-recovery" variant="outline" className="w-full" onClick={() => go("recovery")}>Usar un código de recuperación</Button>
              )}
              {methods.whatsapp && (
                <Button data-testid="login-alt-whatsapp" variant="outline" className="w-full" onClick={() => { setChannel("whatsapp"); setNotice(""); go("otp"); }}>Código por WhatsApp + PIN</Button>
              )}
              {methods.email && (
                <Button data-testid="login-alt-email" variant="outline" className="w-full" onClick={() => { setChannel("email"); setNotice(""); go("otp"); }}>Código por correo + PIN</Button>
              )}
              {!methods.recovery_codes && !methods.whatsapp && !methods.email && (
                <p data-testid="login-alt-none" className="text-xs text-amber-500">No hay otra forma configurada. Pídeme el reset de emergencia por el servidor.</p>
              )}
              <button type="button" className="block w-full text-center text-xs text-muted-foreground underline" onClick={() => go("code")}>Volver</button>
            </div>
          )}

          {step === "recovery" && (
            <form data-testid="login-recovery-form" onSubmit={submitRecovery} className="space-y-4">
              <p className="text-xs text-muted-foreground">Escribe uno de tus 10 códigos de recuperación (cada uno sirve una sola vez).</p>
              <Input data-testid="login-recovery-input" autoFocus placeholder="XXXX-XXXX" value={code} onChange={(e) => setCode(e.target.value)} className="text-center tracking-widest" />
              <Button data-testid="login-recovery-submit" type="submit" disabled={loading || code.replace(/[^a-z0-9]/gi, "").length < 8} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                {submitting("Entrar", "Verificando...")}
              </Button>
              <button type="button" className="block w-full text-center text-xs text-muted-foreground underline" onClick={() => go("alt")}>Volver</button>
            </form>
          )}

          {step === "otp" && (
            <form data-testid="login-otp-form" onSubmit={submitOtp} className="space-y-4">
              <p className="text-xs text-muted-foreground">Te mandamos un código por {CHANNEL[channel]}. Escríbelo junto con tu PIN de 6 dígitos.</p>
              <Button data-testid="login-otp-send" type="button" variant="outline" className="w-full" disabled={loading} onClick={() => sendCode(false)}>
                {sentTo ? "Enviar otro código" : `Enviarme el código por ${CHANNEL[channel]}`}
              </Button>
              <Input data-testid="login-otp-code" inputMode="numeric" maxLength={6} placeholder="Código recibido" value={code} onChange={(e) => setCode(e.target.value)} className="text-center tracking-widest" />
              <Input data-testid="login-otp-pin" type="password" inputMode="numeric" maxLength={6} placeholder="Tu PIN de 6 dígitos" value={pin} onChange={(e) => setPin(e.target.value)} className="text-center tracking-widest" />
              <Button data-testid="login-otp-submit" type="submit" disabled={loading || code.replace(/\D/g, "").length !== 6 || pin.replace(/\D/g, "").length !== 6} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                {submitting("Entrar", "Verificando...")}
              </Button>
              <p className="text-[11px] text-muted-foreground">Al entrar así, los 2 pasos se apagan y el panel te pedirá configurarlos de nuevo.</p>
              <button type="button" className="block w-full text-center text-xs text-muted-foreground underline" onClick={() => { setSentTo(""); setNotice(""); go("alt"); }}>Volver</button>
            </form>
          )}

          {step === "forgot" && (
            <form data-testid="login-forgot-form" onSubmit={submitForgot} className="space-y-4">
              <p className="text-xs text-muted-foreground">Te mandamos un código y lo escribes junto con tu PIN. Luego pones una contraseña nueva.</p>
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email" className="text-muted-foreground text-xs">Email</Label>
                <Input data-testid="login-forgot-email" id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="flex gap-2">
                <select data-testid="login-forgot-channel" aria-label="Canal" value={channel} onChange={(e) => setChannel(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">Correo</option>
                </select>
                <Button data-testid="login-forgot-send" type="button" variant="outline" className="flex-1" disabled={loading || !email} onClick={() => sendCode(true)}>
                  {notice ? "Enviar otro código" : "Enviar código"}
                </Button>
              </div>
              <Input data-testid="login-forgot-code" inputMode="numeric" maxLength={6} placeholder="Código recibido" value={code} onChange={(e) => setCode(e.target.value)} className="text-center tracking-widest" />
              <Input data-testid="login-forgot-pin" type="password" inputMode="numeric" maxLength={6} placeholder="Tu PIN de 6 dígitos" value={pin} onChange={(e) => setPin(e.target.value)} className="text-center tracking-widest" />
              <Input data-testid="login-forgot-new" type="password" autoComplete="new-password" placeholder="Contraseña nueva (mínimo 12 caracteres)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              <Button data-testid="login-forgot-submit" type="submit" disabled={loading || code.replace(/\D/g, "").length !== 6 || pin.replace(/\D/g, "").length !== 6 || newPassword.length < 12} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                {submitting("Cambiar contraseña", "Cambiando...")}
              </Button>
              <button type="button" className="block w-full text-center text-xs text-muted-foreground underline" onClick={() => { setNotice(""); go("password"); }}>Volver</button>
            </form>
          )}

          {error && <p data-testid="login-error" className="text-xs text-amber-500">{error}</p>}
        </div>
      </div>
    </div>
  );
}
